import { ErrorResponse, Log, User, UserManager, WebStorageStateStore } from 'oidc-client-ts'
import type { ClientConfig } from '../data/api'

/**
 * Anmeldung gegen Authentik: Authorization Code Flow mit PKCE.
 *
 * Die App ist ein öffentlicher Client und hat kein Geheimnis. Die Tokens
 * liegen im localStorage, damit ein Neustart der App unterwegs nicht zu einer
 * Anmeldung zwingt, die ohne Netz gar nicht moeglich wäre.
 */

/** Pfad, auf den Authentik nach der Anmeldung zurückschickt. */
export const CALLBACK_PATH = '/auth/callback'

if (import.meta.env.DEV) {
  Log.setLogger(console)
  Log.setLevel(Log.WARN)
}

export function createUserManager(config: ClientConfig): UserManager {
  return new UserManager({
    authority: config.issuer,
    client_id: config.clientId,
    redirect_uri: window.location.origin + CALLBACK_PATH,
    post_logout_redirect_uri: window.location.origin,
    response_type: 'code',
    scope: config.scopes,

    // Das Token wird im Hintergrund erneuert, solange die App offen und online
    // ist. Ohne Netz schlägt das fehl, das fängt der Offline-Modus ab.
    automaticSilentRenew: true,
    accessTokenExpiringNotificationTimeInSeconds: 120,

    // Authentik legt die nötigen Angaben bereits in das Token; ein zusätzlicher
    // Aufruf des UserInfo-Endpunkts bringt nichts und kostet unterwegs Zeit.
    loadUserInfo: false,
    monitorSession: false,

    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
  })
}

/** Ist das Zugriffstoken noch gültig? */
export function isUsable(user: User | null): user is User {
  return !!user && !user.expired && !!user.access_token
}

/**
 * Platz für genau eine laufende Erneuerung.
 *
 * Absichtlich ein einfaches Objekt mit einem Feld: so passt ein useRef hinein,
 * und die Sperre selbst bleibt ohne React und ohne DOM prüfbar.
 */
export interface SingleFlightSlot<T> {
  current: Promise<T> | null
}

/**
 * Lässt höchstens eine Erneuerung gleichzeitig laufen.
 *
 * oidc-client-ts hat keine eigene Sperre: mehrere signinSilent nebeneinander
 * lösen dasselbe Refresh-Token ein. Authentik rotiert Refresh-Tokens, der
 * Verlierer bekommt invalid_grant - und das sieht wie eine endgültig
 * abgelaufene Anmeldung aus, obwohl die andere Erneuerung gerade geglückt ist.
 * Deshalb warten alle Aufrufer auf dasselbe Versprechen.
 *
 * @param slot gemeinsamer Platz aller Aufrufer, die sich abstimmen sollen.
 * @param start startet die Erneuerung, wenn gerade keine läuft.
 */
export function singleFlight<T>(slot: SingleFlightSlot<T>, start: () => Promise<T>): Promise<T> {
  // Nach Erfolg wie nach Fehlschlag muss der Platz wieder frei sein, sonst gibt
  // es keinen zweiten Anlauf mehr.
  slot.current ??= start().finally(() => {
    slot.current = null
  })
  return slot.current
}

/**
 * Alles, was für die Entscheidung nötig ist, ob eine stille Erneuerung
 * überhaupt einen Weg hat.
 */
type RenewableUser = Pick<User, 'refresh_token'>

/**
 * Kann für diesen Nutzer im Hintergrund erneuert werden?
 *
 * Ohne refresh_token nimmt signinSilent den iframe-Weg. Der braucht eine
 * silent_redirect_uri, die diese App nicht hat und als installierbare PWA auch
 * nicht sinnvoll haben kann. Fehlt das Token - etwa weil im Authentik-Provider
 * das Scope-Mapping offline_access nicht ausgewählt ist -, ist jeder Versuch
 * aussichtslos, egal wie gut das Netz gerade ist.
 */
export function canRenewSilently(user: RenewableUser | null | undefined): boolean {
  return typeof user?.refresh_token === 'string' && user.refresh_token.length > 0
}

/**
 * Die stille Erneuerung hat gar keinen Weg. Kein Erreichbarkeitsproblem,
 * sondern ein Zustand, aus dem nur eine neue Anmeldung herausführt.
 */
export class SilentRenewUnavailableError extends Error {
  constructor(message = 'Ohne refresh_token ist keine stille Erneuerung möglich') {
    super(message)
    this.name = 'SilentRenewUnavailableError'
  }
}

/**
 * Merkt sich, wohin der Nutzer eigentlich wollte, damit die Anmeldung ihn nicht
 * auf der Startseite ausspuckt.
 */
export function returnPath(): string {
  return window.location.pathname + window.location.search
}

/**
 * Ergebnis der Einordnung eines gescheiterten Erneuerungsversuchs.
 *
 * Die Werte sind absichtlich die passenden Anmeldezustände: signed-out führt
 * zum Anmeldeschirm, offline lässt die App mit dem lokalen Bestand weiterlaufen.
 */
export type RenewFailure = 'signed-out' | 'offline'

/**
 * Fehlercodes, bei denen der Anmeldedienst geantwortet und die Erneuerung
 * endgültig abgelehnt hat. Nur diese dürfen den Nutzer aussperren.
 *
 * invalid_grant ist der Regelfall: das Refresh-Token ist abgelaufen, verbraucht
 * oder zurückgezogen. Die übrigen Codes bedeuten, dass ohne Zutun des Nutzers
 * kein Token mehr zu bekommen ist.
 */
const TERMINALE_FEHLER = new Set([
  'invalid_grant',
  'invalid_client',
  'invalid_request',
  'invalid_scope',
  'unauthorized_client',
  'unsupported_grant_type',
  'login_required',
  'consent_required',
  'interaction_required',
  'account_selection_required',
  'access_denied',
])

/**
 * Liest den OAuth-Fehlercode aus einem Fehler, falls es einer ist.
 *
 * Neben instanceof wird auch der Name geprüft: oidc-client-ts markiert seine
 * ErrorResponse ausdrücklich damit, und über Bundle-Grenzen oder in Tests ist
 * die Klassenidentität nicht verlässlich.
 */
/** Wortlaut, mit dem oidc-client-ts den fehlenden iframe-Weg meldet. */
const OHNE_IFRAME_ZIEL = 'No silent_redirect_uri configured'

/**
 * Ist die stille Erneuerung an ihrer eigenen Voraussetzung gescheitert?
 *
 * Neben dem eigenen Fehler wird auch der schlichte Error der Bibliothek erkannt:
 * ohne refresh_token fällt sie auf den iframe zurück und bricht dort mit
 * genau dieser Meldung ab. Das ist eine Fehlkonfiguration und ändert sich vom
 * Warten nicht - es wäre sonst eine App, die scheinbar dauerhaft offline ist.
 */
function isSilentRenewUnavailable(err: unknown): boolean {
  if (err instanceof SilentRenewUnavailableError) return true
  if (typeof err !== 'object' || err === null) return false
  const kandidat = err as { name?: unknown; message?: unknown }
  if (kandidat.name === 'SilentRenewUnavailableError') return true
  return typeof kandidat.message === 'string' && kandidat.message.includes(OHNE_IFRAME_ZIEL)
}

function oauthErrorCode(err: unknown): string | null {
  if (err instanceof ErrorResponse) return err.error
  if (typeof err !== 'object' || err === null) return null
  const kandidat = err as { name?: unknown; error?: unknown }
  if (kandidat.name !== 'ErrorResponse') return null
  return typeof kandidat.error === 'string' ? kandidat.error : null
}

/**
 * Ordnet einen Fehler aus signinSilent ein: endgültig gescheiterte Anmeldung
 * oder blosses Erreichbarkeitsproblem.
 *
 * Nur eine ErrorResponse mit einem der terminalen Fehlercodes zählt als echtes
 * Scheitern, dazu der Fall einer von vornherein aussichtslosen Erneuerung.
 * Alles andere - abgebrochenes fetch (TypeError), ErrorTimeout, 5xx vom
 * Anmeldedienst, Captive Portal, unbekannte Fehler - ist ein
 * Erreichbarkeitsproblem und führt in den Offline-Modus.
 *
 * Im Zweifel wird also nicht ausgesperrt: der Nutzer käme unterwegs an keine
 * Anmeldemaske heran, während seine Daten lokal daneben liegen. Ein zu Unrecht
 * angenommener Offline-Zustand kostet dagegen nur den nächsten Erneuerungs-
 * versuch, denn aus offline heraus wird weiter probiert.
 */
export function classifyRenewFailure(err: unknown): RenewFailure {
  // Ohne Weg zur Erneuerung hilft auch das beste Netz nicht mehr.
  if (isSilentRenewUnavailable(err)) return 'signed-out'
  const code = oauthErrorCode(err)
  if (code && TERMINALE_FEHLER.has(code)) return 'signed-out'
  return 'offline'
}

/**
 * Meldung für einen Anmeldeversuch, der schon vor der Weiterleitung scheitert.
 *
 * signinRedirect holt zuerst die Metadaten des Anmeldedienstes. Ohne Netz wirft
 * genau dieser Schritt, und das ist der mit Abstand häufigste Fall: unterwegs,
 * im Funkloch, hinter einem Captive Portal. Die Meldung sagt deshalb nicht nur,
 * dass es nicht ging, sondern auch, was das für den Bestand auf dem Gerät
 * bedeutet - nämlich nichts.
 *
 * Die Einordnung ist dieselbe wie beim Erneuern: alles, was nicht als
 * endgültige Ablehnung des Anmeldedienstes erkennbar ist, gilt als
 * Erreichbarkeitsproblem.
 */
export function anmeldeStartFehler(err: unknown): string {
  if (classifyRenewFailure(err) === 'offline') {
    return (
      'Der Anmeldedienst ist nicht erreichbar. Ohne Verbindung ist keine Anmeldung möglich; ' +
      'warst du hier schon einmal angemeldet, läuft die App mit den gespeicherten Daten weiter. ' +
      'Versuche es erneut, sobald du wieder Netz hast.'
    )
  }
  return (
    'Die Anmeldung liess sich nicht starten: der Anmeldedienst hat sie abgelehnt. ' +
    'Stimmen die Anmeldeparameter des Servers nicht mehr, hilft nur ein Neuladen.'
  )
}
